import React, { useState, useEffect } from 'react';
import { Event } from '../../../types/event.types';
import EventList from '../EventList/EventList';
import { useOrganizer } from '../../../contexts/OrganizerContext';
import { useOrganizerEventsStore } from '../../../stores/organizerEventsStore';
import './OrganizerHomeView.css';

export type ActiveTab = 'active' | 'draft' | 'past';

interface OrganizerHomeViewProps {
  onEventSelect: (event: Event, modifyType?: 'single' | 'series' | 'occurrence') => void;
  onDuplicate?: (duplicatedEvent: Partial<Event>) => void;
  selectedEventId?: string;
  hideHeader?: boolean;
  hideTabs?: boolean;
  activeTab?: ActiveTab;
  onCreateEvent?: () => void;
  /** Optional filtered events - if provided, uses these instead of context events */
  filteredEvents?: Event[];
}

const OrganizerHomeView: React.FC<OrganizerHomeViewProps> = ({
  onEventSelect,
  onDuplicate,
  selectedEventId,
  hideHeader = false,
  hideTabs = false,
  activeTab: externalActiveTab,
  onCreateEvent,
  filteredEvents
}) => {
  const { organizerEvents, eventCounts, eventsLoading } = useOrganizer();

  // Use filtered events if provided, otherwise use context events
  const eventsToDisplay = filteredEvents ?? organizerEvents;
  const loadMetricsByEventIds = useOrganizerEventsStore(state => state.loadMetricsByEventIds);
  const [internalActiveTab, setInternalActiveTab] = useState<ActiveTab>('active');

  // Load metrics when events are available (optimized: uses cached event IDs)
  useEffect(() => {
    if (organizerEvents && organizerEvents.length > 0) {
      const eventIds = organizerEvents.map(e => e.id);
      loadMetricsByEventIds(eventIds);
    }
  }, [organizerEvents, loadMetricsByEventIds]);

  // Use external tab if provided, otherwise use internal state
  const activeTab = externalActiveTab ?? internalActiveTab;
  const setActiveTab = setInternalActiveTab;

  // Reset scroll lors du changement d'onglet
  useEffect(() => {
    const contentEl = document.querySelector('.organizer-home-view__events-content');
    if (contentEl) {
      contentEl.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeTab]);

  const getTabLabel = (tab: ActiveTab) => {
    const counts = {
      active: eventCounts.active,
      draft: eventCounts.draft,
      past: eventCounts.past
    };

    const labels = {
      active: 'Active',
      draft: 'Drafts',
      past: 'Past'
    };

    return `${labels[tab]} (${counts[tab]})`;
  };

  const handleEventEdit = (event: Event, modifyType?: 'single' | 'series' | 'occurrence') => {
    onEventSelect(event, modifyType);
  };

  return (
    <div className="organizer-home-view">
      {/* Section Events avec Onglets */}
      <div className="organizer-home-view__events">
        {/* Header with title and tabs - hidden if both hideHeader and hideTabs are true */}
        {(!hideHeader || !hideTabs) && (
          <div className={`organizer-home-view__events-header ${hideHeader ? 'organizer-home-view__events-header--no-title' : ''}`}>
            {!hideHeader && <h2 className="organizer-home-view__events-title">My Events</h2>}

            {/* Onglets */}
            {!hideTabs && (
              <div className="organizer-home-view__tabs">
                {(['active', 'draft', 'past'] as ActiveTab[]).map((tab) => (
                  <button
                    key={tab}
                    className={`organizer-home-view__tab ${activeTab === tab ? 'organizer-home-view__tab--active' : ''}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {getTabLabel(tab)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="organizer-home-view__events-content">
          {eventsLoading ? (
            <div className="organizer-home-view__loading">
              <p>Loading events...</p>
            </div>
          ) : (
            <EventList
              events={eventsToDisplay}
              activeTab={activeTab}
              onEventSelect={handleEventEdit}
              onDuplicate={onDuplicate}
              onUnpublishOccurrence={(event) => {
                console.log('Unpublishing event:', event.id);
              }}
              hasMore={false}
              isLoadingMore={false}
              onLoadMore={async () => {}}
              selectedEventId={selectedEventId}
              onCreateEvent={onCreateEvent}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizerHomeView;
